import React, { useEffect, useState } from 'react';

import { ContentConflictResolver } from '../services/ContentConflictResolver';
import { ContentVisibilityGuard } from '../services/ContentVisibilityGuard';
import { useSmartModalFallback } from './SmartModal';

interface DebugPanelProps {
  isVisible: boolean;
  onClose: () => void;
}

/**
 * Debug panel for monitoring AutoPanelLayout conflicts and visibility adjustments.
 * Shows real-time information about content conflicts, resolution strategies,
 * and visibility constraints.
 */
// Internal content component for the debug panel
interface DebugPanelContentProps {
  activeTab: 'conflicts' | 'visibility' | 'logs';
  setActiveTab: (tab: 'conflicts' | 'visibility' | 'logs') => void;
  autoRefresh: boolean;
  setAutoRefresh: (value: boolean) => void;
  refreshInterval: number;
  setRefreshInterval: (value: number) => void;
  onClose: () => void;
}

const DebugPanelContent: React.FC<DebugPanelContentProps> = ({
  activeTab,
  setActiveTab,
  autoRefresh,
  setAutoRefresh,
  refreshInterval,
  setRefreshInterval,
  onClose,
}) => {
  // State for debug data
  const [conflictData, setConflictData] = useState(ContentConflictResolver.getDebugInfo());
  const [visibilityData, setVisibilityData] = useState(ContentVisibilityGuard.getDebugInfo());
  const [conflictLogs, setConflictLogs] = useState(ContentConflictResolver.getLogs(20));
  const [visibilityLogs, setVisibilityLogs] = useState(ContentVisibilityGuard.getAdjustmentLogs(20));

  // Auto-refresh data
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setConflictData(ContentConflictResolver.getDebugInfo());
      setVisibilityData(ContentVisibilityGuard.getDebugInfo());
      setConflictLogs(ContentConflictResolver.getLogs(20));
      setVisibilityLogs(ContentVisibilityGuard.getAdjustmentLogs(20));
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  // Safe JSON stringify that handles circular references
  const safeStringify = (obj: any, maxDepth = 3): string => {
    const seen = new WeakSet();

    const replacer = (key: string, value: any, depth = 0): any => {
      // Limit depth to prevent infinite recursion
      if (depth > maxDepth) {
        return '[Max Depth Reached]';
      }

      // Handle null and primitives
      if (value === null || typeof value !== 'object') {
        return value;
      }

      // Check for circular references
      if (seen.has(value)) {
        return '[Circular Reference]';
      }

      seen.add(value);

      // Handle arrays
      if (Array.isArray(value)) {
        return value.map((item, index) => replacer(String(index), item, depth + 1));
      }

      // Handle React components and contexts
      if (value.$$typeof || value._owner || value._context || value.Provider) {
        return '[React Component/Context]';
      }

      // Handle functions
      if (typeof value === 'function') {
        return '[Function]';
      }

      // Handle plain objects
      const result: any = {};
      for (const [k, v] of Object.entries(value)) {
        // Skip potentially problematic keys
        if (k.startsWith('_') || k === 'constructor' || k === 'prototype') {
          continue;
        }
        result[k] = replacer(k, v, depth + 1);
      }

      return result;
    };

    try {
      return JSON.stringify(replacer('', obj), null, 2);
    } catch (error) {
      return `[Stringify Error: ${error instanceof Error ? error.message : 'Unknown error'}]`;
    }
  };

  // Inline styles for simple, theme-agnostic styling
  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: '20px',
    right: '20px',
    width: '500px',
    maxHeight: '80vh',
    background: 'white',
    border: '1px solid #ccc',
    borderRadius: '8px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'monospace',
    fontSize: '12px',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px',
    borderBottom: '1px solid #e0e0e0',
    background: '#f5f5f5',
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '14px',
    fontWeight: 'bold',
  };

  const controlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const tabBarStyle: React.CSSProperties = {
    display: 'flex',
    background: '#f8f8f8',
    borderBottom: '1px solid #e0e0e0',
  };

  const tabStyle: React.CSSProperties = {
    flex: 1,
    padding: '8px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '11px',
  };

  const activeTabStyle: React.CSSProperties = {
    background: 'white',
    borderTop: '2px solid #007acc',
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '8px',
  };

  const clearButtonStyle: React.CSSProperties = {
    padding: '2px 6px',
    fontSize: '11px',
    background: '#ff9800',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
  };

  const closeButtonStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    padding: 0,
    background: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '14px',
  };

  const renderConflictsTab = () => (
    <div>
      <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>Conflict Summary</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Total Conflicts</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{conflictData.totalConflicts}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Recent Activity</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{conflictData.recentConflicts.length}</div>
          </div>
        </div>

        <h5 style={{ margin: '8px 0 4px 0', fontSize: '11px' }}>Resolution Strategies Used</h5>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Object.entries(conflictData.activeResolutions).map(([strategy, count]) => (
            <div
              key={strategy}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px', background: '#fff', borderRadius: '3px' }}
            >
              <span style={{ fontSize: '11px' }}>{strategy}</span>
              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#007acc' }}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>Recent Conflicts</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '300px', overflowY: 'auto' }}>
          {conflictData.recentConflicts.map((conflict, index) => (
            <div
              key={index}
              style={{
                padding: '8px',
                background: '#f5f5f5',
                borderRadius: '3px',
                borderLeft: `3px solid ${conflict.action === 'conflict_detected' ? '#f44336' : conflict.action === 'resolution_applied' ? '#4caf50' : '#2196f3'}`,
              }}
            >
              <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>{formatTimestamp(conflict.timestamp)}</div>
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 4px',
                  background: '#007acc',
                  color: 'white',
                  borderRadius: '2px',
                  fontSize: '10px',
                  marginRight: '4px',
                }}
              >
                {conflict.area}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 'bold', marginRight: '4px' }}>{conflict.action}</span>
              <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                Strategy: {conflict.details.resolution.strategy}
                <br />
                Reason: {conflict.details.resolution.reason}
                <br />
                Conflicting IDs: {conflict.details.conflictingIds.join(', ')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderVisibilityTab = () => (
    <div>
      <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', marginBottom: '8px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>Visibility Summary</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Total Adjustments</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{visibilityData.totalAdjustments}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Constraint Violations</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{visibilityData.constraintViolations}</div>
          </div>
        </div>

        <h5 style={{ margin: '8px 0 4px 0', fontSize: '11px' }}>Fallback Strategies Used</h5>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Object.entries(visibilityData.fallbackStrategiesUsed).map(([strategy, count]) => (
            <div
              key={strategy}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px', background: '#fff', borderRadius: '3px' }}
            >
              <span style={{ fontSize: '11px' }}>{strategy}</span>
              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#007acc' }}>{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>Recent Visibility Adjustments</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '300px', overflowY: 'auto' }}>
          {visibilityData.recentAdjustments.map((adjustment, index) => (
            <div
              key={index}
              style={{
                padding: '8px',
                background: '#f5f5f5',
                borderRadius: '3px',
                borderLeft: `3px solid ${adjustment.fallbackUsed ? '#ff9800' : '#4caf50'}`,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 4px',
                  background: '#007acc',
                  color: 'white',
                  borderRadius: '2px',
                  fontSize: '10px',
                  marginRight: '4px',
                }}
              >
                {adjustment.area}
              </span>
              <span style={{ fontSize: '11px', color: '#666' }}>
                {adjustment.originalStrategy} → {adjustment.appliedStrategy}
              </span>
              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{adjustment.reason}</div>
              {adjustment.fallbackUsed && (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '1px 4px',
                    background: '#ff9800',
                    color: 'white',
                    borderRadius: '2px',
                    fontSize: '9px',
                    marginLeft: '4px',
                  }}
                >
                  FALLBACK
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderLogsTab = () => {
    // Helper function to get timestamp - conflict logs have timestamp, visibility logs use current time
    const getLogTimestamp = (log: any): number => {
      return 'timestamp' in log ? log.timestamp : Date.now();
    };

    // Helper function to format log entry
    const formatLogEntry = (log: any, index: number) => (
      <div
        key={index}
        style={{
          padding: '8px',
          background: '#f5f5f5',
          borderRadius: '3px',
          borderLeft: '3px solid #ccc',
        }}
      >
        <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>{'timestamp' in log ? formatTimestamp(log.timestamp) : 'Recent'}</div>
        {'area' in log && (
          <span
            style={{
              display: 'inline-block',
              padding: '1px 4px',
              background: '#007acc',
              color: 'white',
              borderRadius: '2px',
              fontSize: '10px',
              marginRight: '4px',
            }}
          >
            {log.area}
          </span>
        )}
        {'action' in log ? (
          <span style={{ fontSize: '11px', fontWeight: 'bold', marginRight: '4px' }}>{log.action}</span>
        ) : (
          <span style={{ fontSize: '11px', color: '#666' }}>
            {log.originalStrategy} → {log.appliedStrategy}
          </span>
        )}
        <div style={{ fontSize: '10px', color: '#666', marginTop: '4px', whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
          {'details' in log ? safeStringify(log.details) : log.reason}
        </div>
      </div>
    );

    return (
      <div>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '12px' }}>All System Logs</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '400px', overflowY: 'auto' }}>
          {[...conflictLogs, ...visibilityLogs]
            .sort((a, b) => getLogTimestamp(b) - getLogTimestamp(a))
            .slice(0, 50)
            .map(formatLogEntry)}
        </div>
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>AutoPanelLayout Debug Panel</h3>
        <div style={controlsStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              Auto-refresh
            </label>
            {autoRefresh && (
              <select value={refreshInterval} onChange={(e) => setRefreshInterval(Number(e.target.value))} style={{ fontSize: '11px', padding: '2px 4px' }}>
                <option value={1000}>1s</option>
                <option value={2000}>2s</option>
                <option value={5000}>5s</option>
                <option value={10000}>10s</option>
              </select>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => {
                ContentConflictResolver.clearLogs();
                setConflictData(ContentConflictResolver.getDebugInfo());
                setConflictLogs([]);
              }}
              style={clearButtonStyle}
            >
              Clear Logs
            </button>
            <button onClick={onClose} style={closeButtonStyle}>
              ×
            </button>
          </div>
        </div>
      </div>

      <div style={tabBarStyle}>
        <button style={{ ...tabStyle, ...(activeTab === 'conflicts' ? activeTabStyle : {}) }} onClick={() => setActiveTab('conflicts')}>
          Conflicts ({conflictData.totalConflicts})
        </button>
        <button style={{ ...tabStyle, ...(activeTab === 'visibility' ? activeTabStyle : {}) }} onClick={() => setActiveTab('visibility')}>
          Visibility ({visibilityData.constraintViolations})
        </button>
        <button style={{ ...tabStyle, ...(activeTab === 'logs' ? activeTabStyle : {}) }} onClick={() => setActiveTab('logs')}>
          All Logs ({conflictLogs.length + visibilityLogs.length})
        </button>
      </div>

      <div style={contentStyle}>
        {activeTab === 'conflicts' && renderConflictsTab()}
        {activeTab === 'visibility' && renderVisibilityTab()}
        {activeTab === 'logs' && renderLogsTab()}
      </div>
    </div>
  );
};

// Main DebugPanel component with smart modal integration
export const DebugPanel: React.FC<DebugPanelProps> = ({ isVisible, onClose }) => {
  const [activeTab, setActiveTab] = useState<'conflicts' | 'visibility' | 'logs'>('conflicts');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(2000);

  // Smart modal integration - automatically uses modal if panel can't fit
  const { canShowInline, showAsModal, isInModal } = useSmartModalFallback(
    'RIGHT_SIDEBAR',
    <DebugPanelContent
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      autoRefresh={autoRefresh}
      setAutoRefresh={setAutoRefresh}
      refreshInterval={refreshInterval}
      setRefreshInterval={setRefreshInterval}
      onClose={onClose}
    />,
    'AutoPanel Debug Tools',
  );

  if (!isVisible) return null;

  // If the debug panel should be shown as modal instead of inline, show button to trigger modal
  if (isInModal) {
    return (
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          background: 'rgba(0, 100, 200, 0.9)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 'bold',
        }}
        onClick={showAsModal}
      >
        🔧 Debug Panel (Click for Modal)
      </div>
    );
  }

  // If we can show inline, render the full debug panel content
  return (
    <DebugPanelContent
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      autoRefresh={autoRefresh}
      setAutoRefresh={setAutoRefresh}
      refreshInterval={refreshInterval}
      setRefreshInterval={setRefreshInterval}
      onClose={onClose}
    />
  );
};
